import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, getProfile } from "./api";
import { Shell, Spinner } from "./ui";
import BikeDetail, { STATUS_UI } from "./BikeDetail";
import { Reliability, WorkLog } from "./BikeFleet";

const TILE = {
  operational: "border-emerald-500/60 text-emerald-400",
  faulty: "border-[#ff2e4d] text-[#ff2e4d]",
  awaiting_parts: "border-amber-400/70 text-amber-400",
};
const SHORT = { operational: "OK", faulty: "FAULT", awaiting_parts: "PARTS" };
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { dateStyle: "medium" }) : "—");

export default function Bikes() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("bikes");
  const kind = getProfile()?.kind;
  const canFix = kind === "staff" || kind === "manager";
  const isManager = kind === "manager";

  const load = () => api.get("/bikes").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const tabs = [["bikes", "Bikes"], ["faults", "Open faults"]];
  if (canFix) tabs.push(["work", "Repairs & services"]);
  if (isManager) tabs.push(["reliability", "Bike reliability"]);

  const openBike = (num) => { setTab("bikes"); setSelected(num); };

  if (!data) return <Shell title="Bike health log" back="/staff" wide={tab === "reliability"}><Spinner /></Shell>;

  return (
    <Shell title="Bike health log" back="/staff" wide={tab === "reliability" || tab === "work"}>
      <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="bike-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`bike-tab-${k}`} onClick={() => setTab(k)}
            className={`min-h-[40px] px-4 rounded-full border text-sm font-semibold whitespace-nowrap ${tab === k ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/50"}`}>{l}</button>
        ))}
      </div>

      {tab === "bikes" && (
        <>
          <div data-testid="bike-sticker-hint" className="rounded-xl border border-sky-400/30 bg-sky-400/5 p-3 mb-4 text-sm text-sky-300">
            Find your bike number on the white sticker under the seat, on the rear stem.
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-4" data-testid="bike-counts">
            <span className="text-emerald-400">Operational: <b>{data.counts.operational || 0}</b></span>
            <span className="text-[#ff2e4d]">Faulty / Out of service: <b>{data.counts.faulty || 0}</b></span>
            <span className="text-amber-400">Awaiting parts: <b>{data.counts.awaiting_parts || 0}</b></span>
            <span className="text-white/50">Unavailable: <b className="text-white/80">{data.unavailable}</b></span>
          </div>
          <div className="grid grid-cols-6 gap-2" data-testid="bike-grid">
            {data.bikes.map((b) => (
              <button key={b.bike_number} data-testid={`bike-tile-${b.bike_number}`} onClick={() => setSelected(b.bike_number)}
                aria-label={`Bike ${b.bike_number}: ${data.labels[b.status]}${b.available ? ", available" : ", out of service"}`}
                className={`relative aspect-square min-h-[44px] rounded-xl border bg-[#141414] flex flex-col items-center justify-center active:scale-95 transition ${TILE[b.status] || TILE.operational} ${selected === b.bike_number ? "ring-2 ring-white" : ""}`}>
                <span className={`absolute top-1 right-1 h-1.5 w-1.5 rounded-full ${b.available ? "bg-emerald-400" : "bg-[#ff2e4d]"}`} />
                <span className="font-bold text-base" style={{ fontFamily: "Anton, sans-serif" }}>{String(b.bike_number).padStart(2, "0")}</span>
                <span className="text-[8px] uppercase tracking-wide">{SHORT[b.status] || "OK"}</span>
                {b.total_faults > 0 && <span className="text-[8px] text-white/40">{b.total_faults} fault{b.total_faults > 1 ? "s" : ""}</span>}
              </button>
            ))}
          </div>
          {selected && (() => {
            const b = data.bikes.find((x) => x.bike_number === selected);
            return b?.status_note ? (
              <p className="text-xs text-white/50 mt-2" data-testid="bike-grid-note">
                Bike {String(selected).padStart(2, "0")}: {b.status_note}
              </p>
            ) : null;
          })()}
          {selected && <BikeDetail num={selected} onClose={() => setSelected(null)} onChanged={load} />}
        </>
      )}

      {tab === "faults" && <OpenFaults onOpenBike={openBike} />}
      {tab === "work" && <WorkLog />}
      {tab === "reliability" && <Reliability onOpenBike={openBike} />}
    </Shell>
  );
}

function OpenFaults({ onOpenBike }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/bikes-open-faults").then((r) => setItems(r.data.incidents)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!items) return <Spinner />;
  if (items.length === 0) return <p className="text-sm text-emerald-400" data-testid="no-open-faults">No open faults — all bikes clear.</p>;
  return (
    <div className="space-y-2" data-testid="open-faults-view">
      {items.map((i) => (
        <button key={i.incident_id} onClick={() => onOpenBike(i.bike_number)} data-testid={`open-fault-${i.incident_id}`}
          className="w-full text-left rounded-xl border border-[#ff2e4d]/40 bg-[#ff2e4d]/5 p-3 text-sm active:scale-[0.99] transition">
          <p className="text-[#ff2e4d] font-semibold">Bike {String(i.bike_number).padStart(2, "0")} · {i.incident_id}</p>
          <p className="text-white/80">{i.description}</p>
          <p className="text-xs text-white/40 mt-1">Reported by {i.reporter} · {fmtD(i.reported_at)}</p>
        </button>
      ))}
    </div>
  );
}
